import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Icon, Pagination } from 'semantic-ui-react';
import { fieldStyle } from './adminUi';

/**
 * The pieces every list page shares (대화 내역, 퀴즈 내역, …), so they can't drift: the
 * header band, the desktop filter fields, the filter chips, the table cells and the
 * pagination. Rules behind them: docs/FRONTEND_UI.md.
 */

export const listPageStyle = isMobile => ({
  backgroundColor: '#f9fafb',
  minHeight: isMobile ? 'calc(100dvh - 50px)' : 'calc(100vh - 50px)',
  // Clears the fixed 피드백 보내기 button (bottom 30px + ~44px pill on desktop, 16px + 44px circle
  // on phones), which otherwise sat on the last row's right end: 변경사항 저장, a card's button.
  paddingBottom: isMobile ? '76px' : '96px',
});

/** Desktop list pages run full width with a 20px edge (wide tables); phones get the 14px gutter. */
export const ListContainer = ({ isMobile, children }) => (
  <Container fluid={!isMobile} style={isMobile ? undefined : { padding: '0 20px' }}>
    {children}
  </Container>
);

export const PageBand = ({ isMobile, icon, title, subtitle, actions }) => (
  <div className="page-header-band" data-lp="band" style={{ padding: '16px 0', backgroundColor: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
    <ListContainer isMobile={isMobile}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div className="header-icon-desktop-only" style={{ width: '40px', height: '40px', flexShrink: 0, borderRadius: '50%', backgroundColor: '#F2F2F7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name={icon} className="centered-icon" style={{ color: '#007AFF', fontSize: '18px' }} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h2 style={{ fontSize: '17px', fontWeight: 600, margin: 0, color: '#1d1d1f' }}>{title}</h2>
          <p style={{ fontSize: '13px', color: '#86868b', margin: 0 }}>{subtitle}</p>
        </div>
        {actions && <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>{actions}</div>}
      </div>
    </ListContainer>
  </div>
);

/** A pill button: primary #007AFF, secondary #F2F2F7. `small` for the header band. */
export const pillButton = (primary, small = false) => ({
  border: 'none',
  borderRadius: '999px',
  height: small ? '40px' : '48px',
  padding: small ? '0 16px' : '0 22px',
  fontSize: '14px',
  fontWeight: 600,
  fontFamily: 'inherit',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  lineHeight: 1, // <a> and <button> inherit different line-heights; same box either way
  whiteSpace: 'nowrap',
  flexShrink: 0,
  backgroundColor: primary ? '#007AFF' : '#F2F2F7',
  color: primary ? 'white' : '#1d1d1f',
});

/** Filter chip: dark when active. */
export const chip = active => ({
  border: 'none',
  borderRadius: '999px',
  // An exact height, not padding: padding + line-height came to 28.09px, so a row with chips
  // sat 1px lower than 직원 관리's count-only row.
  height: '28px',
  padding: '0 12px',
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: '14px',
  fontWeight: 600,
  fontFamily: 'inherit',
  cursor: 'pointer',
  lineHeight: 1, // <a> and <button> inherit different line-heights; same box either way
  whiteSpace: 'nowrap',
  backgroundColor: active ? '#1d1d1f' : '#F2F2F7',
  color: active ? 'white' : '#424245',
});

/** Grey 48px field, the shape the dropdowns (CustomDropdown) already have. */
const field = { display: 'flex', alignItems: 'center', gap: '8px', height: '48px', padding: '0 14px', backgroundColor: '#F5F5F7', borderRadius: '12px' };
const bareDate = { border: 'none', outline: 'none', background: 'transparent', fontSize: '14px', fontFamily: 'inherit', color: '#1d1d1f', width: '128px' };

export const DateRangeField = ({ start, end, onChange, onClear, label }) => (
  <div data-lp="date" style={{ ...field, gap: '6px', flexShrink: 0 }}>
    <Icon name="calendar alternate outline" className="centered-icon" style={{ color: '#86868b', fontSize: '15px' }} />
    {/* Optional: what the range applies to, when it isn't the whole list (직원 관리). */}
    {label && <span style={{ fontSize: '13px', fontWeight: 600, color: '#86868b', whiteSpace: 'nowrap' }}>{label}</span>}
    <input type="date" aria-label="시작일" value={start || ''} onChange={e => onChange('start_date', e.target.value)} style={bareDate} />
    <span style={{ color: '#86868b' }}>–</span>
    <input type="date" aria-label="종료일" value={end || ''} onChange={e => onChange('end_date', e.target.value)} style={bareDate} />
    {(start || end) && (
      <Icon name="close" link className="centered-icon" title="기간 초기화" onClick={onClear} style={{ color: '#86868b', fontSize: '13px' }} />
    )}
  </div>
);

const phoneDate = { flex: 1, minWidth: 0, height: '40px', border: '1px solid #d2d2d7', borderRadius: '10px', padding: '0 8px', fontSize: '14px', fontFamily: 'inherit', background: 'white' };

/** Phones: the date range inside a list's filter panel. */
export const PhoneDateRange = ({ start, end, onChange, onClear }) => (
  <div data-lp="phone-dates" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#424245' }}>
    <input type="date" aria-label="시작일" value={start || ''} onChange={e => onChange('start_date', e.target.value)} style={phoneDate} />
    <span>~</span>
    <input type="date" aria-label="종료일" value={end || ''} onChange={e => onChange('end_date', e.target.value)} style={phoneDate} />
    {(start || end) && (
      <button type="button" onClick={onClear} style={{ border: 'none', background: 'none', padding: 0, color: '#007AFF', fontSize: '14px', fontFamily: 'inherit', whiteSpace: 'nowrap', cursor: 'pointer' }}>
        초기화
      </button>
    )}
  </div>
);

export const SearchField = ({ value, onChange, onSubmit, placeholder, isMobile }) => (
  <div data-lp="search" style={{ ...field, flex: '1 1 200px', minWidth: 0, padding: '0 12px', height: isMobile ? '40px' : '48px' }}>
    <Icon name="search" className="centered-icon" style={{ color: '#86868b' }} />
    <input
      type="search"
      enterKeyHint="search"
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
      onKeyDown={e => e.key === 'Enter' && onSubmit()}
      style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontSize: '16px', fontFamily: 'inherit' }}
    />
  </div>
);

/**
 * Phones: the square button beside the search field (filters, export). `on` = its panel is
 * open; `set` = a filter in it is applied (blue glyph, so a closed panel still says so).
 */
export const SquareButton = ({ icon, label, on, set, onClick, loading, disabled }) => (
  <button
    type="button"
    data-lp="square"
    aria-label={label}
    title={label}
    onClick={onClick}
    disabled={disabled}
    style={{ flexShrink: 0, border: 'none', borderRadius: '12px', width: '40px', height: '40px', padding: 0, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: on || set ? '#EAF3FF' : '#F5F5F7', color: set ? '#007AFF' : '#424245', opacity: disabled ? 0.6 : 1 }}
  >
    <Icon name={loading ? 'spinner' : icon} loading={loading} className="centered-icon" style={{ fontSize: '15px' }} />
  </button>
);

/** Chip row: groups separated by a thin rule, the total on the right.
    (data-lp attributes here and above: hooks for the cross-page style check.) */
export const ChipRow = ({ groups, count, unit = '개' }) => (
  <div data-lp="chiprow" style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', margin: '12px 0', minHeight: '28px' }}>
    {groups.map((g, i) => (
      <React.Fragment key={i}>
        {i > 0 && <span style={{ width: '1px', height: '18px', backgroundColor: '#d2d2d7', margin: '0 2px' }} />}
        {g}
      </React.Fragment>
    ))}
    <div style={{ flex: 1 }} />
    {count != null && <span data-lp="count" style={{ fontSize: '13px', color: '#86868b', whiteSpace: 'nowrap' }}>총 {count}{unit}</span>}
  </div>
);

// Table cells. The first column gets a 20px inset so it lines up with the card's rounding.
export const th = { textAlign: 'left', padding: '11px 12px', borderBottom: '1px solid rgba(34,36,38,.15)', color: '#86868b', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap', background: 'white' };
export const td = { padding: '12px', borderBottom: '1px solid rgba(0,0,0,0.05)', fontSize: '14px', color: '#1d1d1f', verticalAlign: 'middle' };
export const firstCell = cell => ({ ...cell, padding: cell === th ? '11px 12px 11px 20px' : '12px 12px 12px 20px' });

export const SortTh = ({ label, field: f, ordering, onSort, style }) => {
  const active = ordering?.replace('-', '') === f;
  const icon = ordering === f ? 'sort up' : ordering === `-${f}` ? 'sort down' : 'sort';
  return (
    <th style={{ ...th, cursor: 'pointer', ...style }} onClick={() => onSort(f)}>
      {label} <Icon name={icon} style={{ color: active ? '#1d1d1f' : '#c7c7cc' }} />
    </th>
  );
};

// ---- Row content: the same in a desktop row and a phone card ----

/** List scores: green from 80, amber from 50, red below — one score, one colour everywhere. */
export const scoreColor = score => (score >= 80 ? '#1A7F4B' : score >= 50 ? '#B25E00' : '#C0392B');
/** Scores read as whole points ("86점") in every list. */
export const scoreText = score => `${Math.round(score)}점`;

const TAG_TONES = {
  blue: ['#EAF3FF', '#007AFF'],
  gray: ['#F2F2F7', '#6e6e73'],
  green: ['#E8F8EE', '#1E8E3E'],
  red: ['#FDECEC', '#D93025'],
  amber: ['#FFF4E5', '#B25E00'],
};

/** A status tag (평가 / 연습, 정답 / 오답, 객관식, 승인됨 …): one size and shape in every list. */
export const Tag = ({ tone = 'gray', children, title }) => (
  <span
    title={title}
    style={{ display: 'inline-flex', alignItems: 'center', height: '22px', padding: '0 8px', borderRadius: '999px', fontSize: '12px', fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap', flexShrink: 0, backgroundColor: TAG_TONES[tone][0], color: TAG_TONES[tone][1] }}
  >
    {children}
  </span>
);

// Phone card anatomy: a title row (title left, tags right), 12px grey meta lines, a 13px
// body, then action buttons. Desktop cells use the same colours and score/date formats.
export const cardTitle = { flex: 1, minWidth: 0, fontSize: '15px', fontWeight: 600, color: '#1d1d1f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
export const cardMeta = { fontSize: '12px', color: '#86868b', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
export const cardBody = { fontSize: '13px', color: '#424245', lineHeight: 1.5, marginTop: '8px' };
export const cardActions = { display: 'flex', gap: '8px', marginTop: '12px' };
export const cardHeader = { display: 'flex', alignItems: 'center', gap: '6px' };

/** A card's action button: 32px pill, blue for the one that leads, grey otherwise. */
export const cardAction = (primary, fill = false) => ({
  border: 'none',
  borderRadius: '999px',
  height: '32px',
  padding: '0 14px',
  fontSize: '13px',
  fontWeight: 600,
  fontFamily: 'inherit',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  lineHeight: 1, // <a> and <button> inherit different line-heights; same box either way
  whiteSpace: 'nowrap',
  flex: fill ? 1 : '0 0 auto',
  backgroundColor: primary ? '#007AFF' : '#F2F2F7',
  color: primary ? 'white' : '#1d1d1f',
});

/** Desktop cells: dates and durations in grey 13px, as in the cards' meta line. */
export const mutedCell = { color: '#86868b', fontSize: '13px', whiteSpace: 'nowrap' };

/** One row of a phone list (대화 내역, 퀴즈 내역, 직원 관리 cards). */
export const listCard = {
  backgroundColor: 'white',
  borderRadius: '16px',
  border: '1px solid rgba(0,0,0,0.05)',
  boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
  padding: '14px 16px',
};

/** The rounded white card a list's table sits in. */
export const tableCard = {
  backgroundColor: 'white',
  borderRadius: '16px',
  border: '1px solid rgba(0,0,0,0.05)',
  boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
  overflow: 'hidden',
};

export const ListPagination = ({ page, count, pageSize, onChange }) =>
  count > pageSize ? (
    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
      <Pagination
        activePage={page}
        totalPages={Math.ceil(count / pageSize)}
        onPageChange={(e, { activePage }) => onChange(activePage)}
        size="mini"
        firstItem={null}
        lastItem={null}
        prevItem={{ content: <Icon name="angle left" />, icon: true }}
        nextItem={{ content: <Icon name="angle right" />, icon: true }}
      />
    </div>
  ) : null;

// ---- Whole-page pieces, so a list page is configuration, not markup ----

/** Grey page + header band + container. `actions`: the band's right-side buttons (desktop). */
export const ListPageShell = ({ isMobile, icon, title, subtitle, actions, children }) => (
  <div style={listPageStyle(isMobile)}>
    <PageBand isMobile={isMobile} icon={icon} title={title} subtitle={subtitle} actions={actions} />
    <ListContainer isMobile={isMobile}>{children}</ListContainer>
  </div>
);

/** The band's action button: `primary` for the page's main action (새 …), grey otherwise.
    `compact` (phones): the 40px square, blue-tinted when primary. */
export const BandButton = ({ primary, icon, children, onClick, disabled, loading, compact }) =>
  compact ? (
    <SquareButton icon={icon} label={children} set={primary} onClick={onClick} loading={loading} disabled={disabled} />
  ) : (
  <button type="button" onClick={onClick} disabled={disabled} style={{ ...pillButton(primary, true), opacity: disabled ? 0.6 : 1 }}>
    {children}
    {(icon || loading) && <Icon name={loading ? 'spinner' : icon} loading={loading} className="centered-icon" />}
  </button>
  );

const NOTE_TONES = {
  blue: ['#EAF2FF', '#0B4FB3', 'info circle'],
  green: ['#E8F8EE', '#1E6B34', 'check circle'],
  red: ['#FDECEC', '#B42318', 'exclamation circle'],
  amber: ['#FFF4E5', '#8A4B00', 'exclamation triangle'],
};

/** A note above a list or form: blue info (default), green saved, red error, amber caution.
    The icon sits on the first line's centre (19.5px line). */
export const ListNote = ({ tone = 'blue', children }) => {
  const [bg, fg, icon] = NOTE_TONES[tone];
  return (
    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', backgroundColor: bg, borderRadius: '12px', padding: '12px 16px', fontSize: '13px', lineHeight: 1.5, color: fg }}>
      <span style={{ height: '19.5px', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <Icon name={icon} className="centered-icon" style={{ fontSize: '14px' }} />
      </span>
      <span>{children}</span>
    </div>
  );
};

/** Form inputs: adminUi's fieldStyle at an exact 42px, so inputs, dropdown-less selects and
    ToggleFields line up; textareas grow from it. */
export const inputStyle = { ...fieldStyle, height: '42px' };
/** A read-only value in the same box as an input (a linked conversation, a source link). */
export const valueBoxStyle = { ...inputStyle, display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 };
export const textareaStyle = { ...fieldStyle, minHeight: '110px', resize: 'vertical', lineHeight: 1.5 };
export const monoTextareaStyle = { ...textareaStyle, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '13px' };

/** An on/off switch (활성 상태): site blue when on. */
export const Toggle = ({ checked, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={() => onChange(!checked)}
    style={{ position: 'relative', width: '44px', height: '26px', flexShrink: 0, border: 'none', borderRadius: '999px', padding: 0, cursor: 'pointer', backgroundColor: checked ? '#007AFF' : '#d2d2d7', transition: 'background-color .15s' }}
  >
    <span style={{ position: 'absolute', top: '3px', left: checked ? '21px' : '3px', width: '20px', height: '20px', borderRadius: '50%', backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left .15s' }} />
  </button>
);

/** A form field holding a switch and its state word, the same box as an input. */
export const ToggleField = ({ checked, onChange, on = '활성', off = '비활성' }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#F5F5F7', borderRadius: '10px', padding: '0 14px', height: '42px' }}>
    <span style={{ fontSize: '14px', color: checked ? '#1d1d1f' : '#86868b' }}>{checked ? on : off}</span>
    <Toggle checked={checked} onChange={onChange} label={checked ? on : off} />
  </div>
);

/**
 * A row's actions, one family on every list and detail page (the mix of adminUi Pills at
 * 29px in #0091FF, red-tinted squares and filled icons is what read as inconsistent):
 * RowAction — a 32px grey icon square, outline icons; RowButton — a 32px text pill, site blue
 * when it leads, grey otherwise. Destructive ones confirm in a modal, so they aren't red here.
 */
export const RowAction = ({ icon, title, onClick, disabled }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    disabled={disabled}
    onClick={e => { e.stopPropagation(); if (!disabled) onClick(e); }}
    style={{ width: '32px', height: '32px', padding: 0, border: 'none', borderRadius: '999px', cursor: disabled ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, backgroundColor: '#F2F2F7', color: '#1d1d1f', opacity: disabled ? 0.45 : 1, flexShrink: 0 }}
  >
    <Icon name={icon} className="centered-icon" style={{ fontSize: '13px' }} />
  </button>
);

export const RowButton = ({ primary, icon, children, onClick, disabled, title }) => (
  <button
    type="button"
    title={title}
    disabled={disabled}
    onClick={e => { e.stopPropagation(); if (!disabled) onClick(e); }}
    style={{ ...cardAction(primary), opacity: disabled ? 0.45 : 1, cursor: disabled ? 'default' : 'pointer' }}
  >
    {icon && <Icon name={icon} className="centered-icon" />}
    {children}
  </button>
);

/** A rate as "86% 6/7": the percent coloured as a score, the counts grey (제품 지식, 퀴즈). */
export const RatioScore = ({ num, den, unit = '' }) => {
  if (!den) return <span style={{ color: '#86868b' }}>—</span>;
  const pct = Math.round((num / den) * 100);
  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      <span style={{ fontWeight: 600, color: scoreColor(pct) }}>{pct}%</span>
      <span style={{ fontSize: '12px', color: '#86868b' }}> {num}/{den}{unit}</span>
    </span>
  );
};

const emptyState = { textAlign: 'center', padding: '40px 16px', color: '#86868b', fontSize: '14px' };

/**
 * A list's rows: a table in `tableCard` on desktop, `listCard`s on phones — from one set of
 * columns, so a page writes no table or card markup of its own.
 *
 * columns: [{ key, label, render(row), sort ('ordering' field), align ('center'|'right'),
 *             muted (grey 13px, for dates/durations), nowrap, maxWidth }]
 * card:    { title(row), tags(row) → [node], meta(row) → [string|node], body(row), actions(row) → [node] }
 */
export const ListTable = ({ isMobile, rows, columns, card, rowKey = r => r.id, onRowClick, ordering, onSort, loading, empty = '항목이 없습니다.' }) => {
  if (loading) return <div style={emptyState}>불러오는 중…</div>;
  if (!rows.length) return <div style={isMobile ? { ...listCard, ...emptyState } : { ...tableCard, ...emptyState }}>{empty}</div>;

  if (isMobile) {
    return (
      <div style={cardStack}>
        {rows.map(r => {
          const list = f => ((f && f(r)) || []).filter(Boolean);
          const tags = list(card.tags);
          const meta = list(card.meta);
          const body = card.body && card.body(r);
          const actions = list(card.actions);
          return (
            <div key={rowKey(r)} role={onRowClick ? 'button' : undefined} onClick={onRowClick ? () => onRowClick(r) : undefined} style={{ ...listCard, cursor: onRowClick ? 'pointer' : 'default' }}>
              <div style={cardHeader}>
                <div style={cardTitle}>{card.title(r)}</div>
                {tags}
              </div>
              {meta.length > 0 && <div style={cardMeta}>{meta.map((m, i) => <React.Fragment key={i}>{i > 0 && ' · '}{m}</React.Fragment>)}</div>}
              {body && <div style={{ ...cardBody, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', wordBreak: 'break-word' }}>{body}</div>}
              {actions.length > 0 && <div style={cardActions} onClick={e => e.stopPropagation()}>{actions}</div>}
            </div>
          );
        })}
      </div>
    );
  }

  const align = c => (c.align ? { textAlign: c.align } : null);
  return (
    <div style={tableCard}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {columns.map((c, i) => {
                const style = { ...(i === 0 ? firstCell(th) : null), ...align(c) };
                return c.sort
                  ? <SortTh key={c.key} label={c.label} field={c.sort} ordering={ordering} onSort={onSort} style={style} />
                  : <th key={c.key} style={{ ...th, ...style }}>{c.label}</th>;
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={rowKey(r)} onClick={onRowClick ? () => onRowClick(r) : undefined} style={{ cursor: onRowClick ? 'pointer' : 'default' }}>
                {columns.map((c, i) => (
                  <td
                    key={c.key}
                    style={{ ...(i === 0 ? firstCell(td) : td), ...(c.muted ? mutedCell : null), ...(c.nowrap ? { whiteSpace: 'nowrap' } : null), ...(c.maxWidth ? { maxWidth: c.maxWidth } : null), ...align(c) }}
                  >
                    {c.render(r)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/** Two lines, then "…" — long text in a table cell or card body. */
export const clamp2 = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' };
/** An empty value in a cell. */
export const Dash = () => <span style={{ color: '#86868b' }}>—</span>;

/**
 * A row's own page (페르소나, 평가 항목 그룹 …): the band with a back chip, then content at a
 * readable width. Back returns to the list as it was (filters, scroll); opened directly, to `backTo`.
 */
export const DetailPageShell = ({ isMobile, title, subtitle, backTo, actions, wide, children }) => {
  const navigate = useNavigate();
  const back = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate(backTo));
  // `wide`: tables and forms (파트너 상세, 제품 상세 …) run full width like the lists; otherwise
  // a reading width (a persona, a criteria group, a ticket thread).
  // A function, not a component: a component defined here would be a new type each render and
  // remount the page (closing opened cards, dropping edits).
  const body = c => (wide ? <ListContainer isMobile={isMobile}>{c}</ListContainer> : <Container text={!isMobile}>{c}</Container>);
  return (
    <div style={listPageStyle(isMobile)}>
      <div className="page-header-band" data-lp="band" style={{ padding: '16px 0', backgroundColor: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
        {body(
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button type="button" onClick={back} title="목록으로" aria-label="목록으로" style={{ width: '40px', height: '40px', flexShrink: 0, border: 'none', borderRadius: '50%', backgroundColor: '#F2F2F7', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="arrow left" className="centered-icon" style={{ color: '#007AFF', fontSize: '17px' }} />
            </button>
            <div style={{ minWidth: 0, flex: 1 }}>
              <h2 style={{ fontSize: '17px', fontWeight: 600, margin: 0, color: '#1d1d1f' }}>{title}</h2>
              {subtitle && <p style={{ fontSize: '13px', color: '#86868b', margin: 0 }}>{subtitle}</p>}
            </div>
            {actions && <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>{actions}</div>}
          </div>,
        )}
      </div>
      {body(children)}
    </div>
  );
};

/** A detail page's tabs: the filter-chip style with a count, on the 28px chip row. */
export const DetailTabs = ({ tabs, active, onChange }) => (
  <ChipRow
    groups={[
      tabs.map((t, i) => (
        <button key={t.label} type="button" onClick={() => onChange(i)} style={chip(active === i)}>
          {t.label}
          {t.count != null && <span style={{ marginLeft: '6px', opacity: 0.6 }}>{t.count}</span>}
        </button>
      )),
    ]}
  />
);

/** Vertical rhythm inside a detail page: 12px between blocks (note, actions, table, sections). */
// minmax(0, 1fr): a grid's implicit column is as wide as its widest unbreakable content, so
// one card with a long nowrap line (a meta row of name · partner) widened every card past a
// phone's screen and the page scrolled sideways. Every vertical stack of cards uses these.
export const stack = { display: 'grid', gap: '12px', gridTemplateColumns: 'minmax(0, 1fr)' };
/** Phone card lists: the cards in one column that can shrink (see stack). */
export const cardStack = { display: 'grid', gap: '10px', gridTemplateColumns: 'minmax(0, 1fr)' };

/** A form's section: the report's section title, then a white card of fields. */
export const FormSection = ({ title, children }) => (
  <section>
    <div style={{ fontSize: '16px', fontWeight: 700, color: '#1d1d1f', margin: '0 2px 10px' }}>{title}</div>
    <div style={{ ...listCard, padding: '16px' }}>{children}</div>
  </section>
);

/** Two columns on desktop, one on phones. minmax(0, …): a plain 1fr won't shrink below its
    content, so one long value (a linked conversation's title) widened the page past a phone. */
export const FormGrid = ({ isMobile, children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(0, 1fr)', gap: '14px 16px' }}>{children}</div>
);

/** A labelled field; `full` spans both columns. Inputs use adminUi's fieldStyle. */
export const FormField = ({ label, hint, full, children }) => (
  <div style={full ? { gridColumn: '1 / -1' } : undefined}>
    <div style={{ fontSize: '13px', fontWeight: 600, color: '#1d1d1f', marginBottom: '6px' }}>{label}</div>
    {children}
    {hint && <div style={{ fontSize: '12px', color: '#86868b', marginTop: '6px' }}>{hint}</div>}
  </div>
);

/** The form's save button, right-aligned under the sections. */
export const SaveBar = ({ onSave, disabled, saving, label = '변경사항 저장' }) => (
  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
    <button type="button" onClick={onSave} disabled={disabled || saving} style={{ ...pillButton(true), opacity: disabled || saving ? 0.5 : 1, cursor: disabled || saving ? 'default' : 'pointer' }}>
      {saving ? '저장 중…' : label}
    </button>
  </div>
);

/** Read-only text in a grey box: JSON, ids (지원 요청 컨텍스트). */
export const MonoBlock = ({ children }) => (
  <pre style={{ margin: 0, backgroundColor: '#F5F5F7', borderRadius: '10px', padding: '12px 14px', fontSize: '12px', lineHeight: 1.6, color: '#424245', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all', overflowX: 'auto' }}>{children}</pre>
);

/** A list's row checkbox (bulk actions): native, site blue. */
export const RowCheck = ({ checked, disabled, onChange, label }) => (
  <input type="checkbox" aria-label={label} checked={checked} disabled={disabled} onClick={e => e.stopPropagation()} onChange={onChange} style={{ width: '16px', height: '16px', margin: 0, accentColor: '#007AFF', cursor: disabled ? 'default' : 'pointer', verticalAlign: 'middle' }} />
);

/** The destructive version of a 48px pill (요청 종료). */
export const dangerPill = { ...pillButton(false), backgroundColor: '#FDECEC', color: '#D93025' };

/** A file input drawn as a grey card-action pill + the chosen file's name (the native
    "Choose File" control looked like a different app). */
export const FilePick = ({ accept, file, onChange, label = '이미지 첨부' }) => {
  const ref = React.useRef(null);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
      <input ref={ref} type="file" accept={accept} onChange={e => onChange(e.target.files?.[0] || null)} style={{ display: 'none' }} />
      <button type="button" onClick={() => ref.current?.click()} style={cardAction(false)}>
        <Icon name="image outline" className="centered-icon" />
        {label}
      </button>
      {file ? (
        <>
          <span style={{ fontSize: '13px', color: '#424245', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
          <Icon name="close" link className="centered-icon" title="첨부 취소" onClick={() => { if (ref.current) ref.current.value = ''; onChange(null); }} style={{ color: '#86868b', fontSize: '12px' }} />
        </>
      ) : (
        <span style={{ fontSize: '13px', color: '#86868b' }}>선택 사항 · JPG, PNG</span>
      )}
    </div>
  );
};
